import {
  ENDPOINT_FARMER_CONTRACTS_BY_TIN,
  ENDPOINT_FARMER_CONTRACTS_BY_TIN_ANALYSIS,
  ENDPOINT_GRAIN_RECEPTION,
  ENDPOINT_GRAIN_RECEPTION_BY_FARMER,
  ENDPOINT_GRAIN_RECEPTION_BY_PAGE,
  ENDPOINT_GRAIN_RECEPTION_COUNTS,
  ENDPOINT_GRAIN_RECEPTION_DETAIL,
  ENDPOINT_GRAIN_RECEPTION_MANAGE_FARMER_CONTRACT,
  ENDPOINT_GRAIN_RECEPTION_SAVE_ANALYSIS,
  ENDPOINT_GRAIN_RECEPTION_UPDATE_PARTY,
  ENDPOINT_PARTIES,
  ENDPOINT_RECEPTION,
  ENDPOINT_RECEPTION_BY_PAGE,
  ENDPOINT_RECEPTION_SLUG,
} from '@/common/constants';
import { HttpApi } from '@/common/http';
import { IParty } from '@/features/parties/models';
import { create } from 'zustand';
import { IFarmerContract } from '../components/general/models';
import { ICount, ILoad, IReceptionByPage, IReceptionStore } from '../models';

const httpApi = new HttpApi();
export const useLoadStore = create<IReceptionStore>((set) => ({
  load: null,
  loadGrain: null,
  grainCount: null,
  party: [],
  grainContracts: [],
  loadList: null,
  loadLoading: true,
  countLoading: true,
  loadGrainLoading: true,
  loadListLoading: true,
  partyLoading: true,
  isManagePartiesLoading: false,
  loadGrainList: null,
  loadGrainListLoading: true,
  receptionGrainLoading: false,
  isManageGrainPartiesLoading: false,
  farmerByTaxnumbers: [],
  analysisByGrainTaxnumbers: [],
  analysisByGrainTaxnumbersLoading: false,
  grainAnalysisLoading: false,
  fetchLoadList: async (params) => {
    set({
      loadListLoading: true,
    });
    const response = await httpApi.get<IReceptionByPage>(
      ENDPOINT_RECEPTION_BY_PAGE,
      { ...params },
    );
    set({
      loadList: response,
    });

    setTimeout(() => {
      set({
        loadListLoading: false,
      });
    }, 300);
  },
  fetchFarmerGrain: async (stir: number) => {
    const response = await httpApi.get<IFarmerContract[]>(
      ENDPOINT_FARMER_CONTRACTS_BY_TIN.replace('{tin}', stir.toString()),
    );
    set({ grainContracts: response });
    return response;
  },
  fetchFarmerByTaxnumber: async (taxNumber: number) => {
    const response = await httpApi.get<IFarmerContract[]>(
      ENDPOINT_FARMER_CONTRACTS_BY_TIN_ANALYSIS.replace(
        '{tin}',
        taxNumber.toString(),
      ),
    );
    set({ grainContracts: response });
  },
  fetchLoadGrainCount: async () => {
    const response = await httpApi.get<ICount>(ENDPOINT_GRAIN_RECEPTION_COUNTS);
    set({
      grainCount: response,
    });
  },
  fetchGrainDataDetail: async (id) => {
    set({ loadGrainLoading: true });
    const res = await httpApi.get<ILoad>(
      ENDPOINT_GRAIN_RECEPTION_DETAIL.replace('{id}', id),
    );
    set({ loadGrain: res });
    setTimeout(() => {
      set({ loadGrainLoading: false });
    }, 300);
    return res;
  },
  fetchDataDetail: async (id) => {
    set({ loadLoading: true });
    const res = await httpApi.get<ILoad>(`${ENDPOINT_RECEPTION}/${id}`);
    set({ load: res });
    setTimeout(() => {
      set({ loadLoading: false });
    }, 300);
    return res;
  },
  fetchParty: async (id: string) => {
    set({ partyLoading: true });
    try {
      const response = await httpApi.get<IParty[]>(ENDPOINT_PARTIES, {
        crop_type_id: id,
      });
      set({ party: response });
    } catch (error) {
      console.error('Failed to fetch parties:', error);
      throw error;
    } finally {
      setTimeout(() => {
        set({ partyLoading: false });
      }, 300);
    }
  },
  manageParties: async (data, id) => {
    set({ isManagePartiesLoading: true });
    try {
      const res = await httpApi.put(
        ENDPOINT_RECEPTION_SLUG.replace('{slug}', id!),
        data,
      );
      return res;
    } finally {
      set({ isManagePartiesLoading: false });
    }
  },
  manageGrainParties: async (data, id) => {
    set({ isManageGrainPartiesLoading: true });
    try {
      const res = await httpApi.put(
        ENDPOINT_GRAIN_RECEPTION_UPDATE_PARTY.replace('{id}', id!),
        data,
      );
      return res;
    } finally {
      set({ isManageGrainPartiesLoading: false });
    }
  },
  fetchLoadGrainList: async (params) => {
    set({
      loadGrainListLoading: true,
    });
    const response = await httpApi.get<IReceptionByPage>(
      ENDPOINT_GRAIN_RECEPTION_BY_PAGE,
      { ...params },
    );

    set({
      loadGrainList: response,
    });

    setTimeout(() => {
      set({
        loadGrainListLoading: false,
      });
    }, 300);
  },
  saveGrainReception: async (data) => {
    set({ receptionGrainLoading: true });
    try {
      const res = await httpApi.post(ENDPOINT_GRAIN_RECEPTION, data);
      set({ load: res });
      return res;
    } finally {
      set({ receptionGrainLoading: false });
    }
  },
  fetchFarmerGrainPhysical: async (stir: number) => {
    const response = await httpApi.get<IFarmerContract[]>(
      ENDPOINT_FARMER_CONTRACTS_BY_TIN_ANALYSIS.replace(
        '{tin}',
        stir.toString(),
      ),
    );
    set({ grainContracts: response });
    return response;
  },
  fetchAnalysisByGrainTaxnumbers: async (
    stir: string,
    params: Record<string, any>,
  ) => {
    set({ analysisByGrainTaxnumbersLoading: true });
    try {
      const response = await httpApi.get<any[]>(
        ENDPOINT_GRAIN_RECEPTION_BY_FARMER.replace('{tin}', stir),
        params,
      );
      set(() => ({
        farmerByTaxnumbers: response,
        analysisByGrainTaxnumbers: response,
      }));
    } finally {
      set({ analysisByGrainTaxnumbersLoading: false });
    }
  },
  saveGrainAnalysis: async (data: any) => {
    set({ grainAnalysisLoading: true });
    try {
      const response = await httpApi.put(
        ENDPOINT_GRAIN_RECEPTION_SAVE_ANALYSIS,
        data,
      );
      return response;
    } finally {
      set({ grainAnalysisLoading: false });
    }
  },
  manageGrainContract: async (data, id) => {
    set({ isManageGrainContractLoading: true });
    try {
      const res = await httpApi.put(
        ENDPOINT_GRAIN_RECEPTION_MANAGE_FARMER_CONTRACT.replace('{id}', id!),
        data,
      );
      return res;
    } finally {
      set({ isManageGrainContractLoading: false });
    }
  },
}));
